import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

// Create auth context
const AuthContext = createContext(null);

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is already logged in on mount
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('user');
    
    if (token && storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('Failed to parse stored user data');
        localStorage.removeItem('user');
        localStorage.removeItem('authToken');
      }
    }
    
    setLoading(false);
  }, []);

  // Login function
  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    
    try {
      // In a real implementation, this would be an API call
      // const response = await axios.post('http://localhost:8000/api/auth/login', { email, password });
      
      // For demo purposes, we'll simulate the API response
      const mockResponse = {
        data: {
          token: 'mock-auth-token',
          user: {
            id: 1,
            name: email.includes('admin') ? 'Admin User' : 
                  email.includes('store') ? 'Store Owner' : 'Customer',
            email: email,
            role: email.includes('admin') ? 'admin' : 
                  email.includes('store') ? 'store_owner' : 'customer',
          }
        }
      };
      
      // Store token and user data
      localStorage.setItem('authToken', mockResponse.data.token);
      localStorage.setItem('user', JSON.stringify(mockResponse.data.user));
      
      // Update state
      setUser(mockResponse.data.user);
      
      return mockResponse.data.user;
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Register function
  const register = async (userData) => {
    setLoading(true);
    setError(null);
    
    try {
      // In a real implementation, this would be an API call
      // const response = await axios.post('http://localhost:8000/api/auth/register', userData);
      
      // For demo purposes, we'll simulate the API response
      const mockResponse = {
        data: {
          message: 'Registration successful'
        }
      };
      
      return mockResponse.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    // In a real implementation, you might want to call an API endpoint to invalidate the token
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setUser(null);
  };

  // Check if user has a specific role
  const hasRole = (role) => {
    return user && user.role === role;
  };

  // Context value
  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    hasRole,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
